// Phase 282c — Weekly Activation Recap tile for the academy_owner dashboard.
// Pulls the live `/api/weekly-activation-digest/preview` payload (the same data
// that ships in the Monday 09:00 IST email) and displays it inline. Includes a
// "Preview Email" button that opens the rendered HTML in a Dialog so owners
// can rehearse what their inbox will look like before the cron fires.
import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Sparkles, Mail, TrendingUp } from 'lucide-react';

export default function WeeklyActivationTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/weekly-activation-digest/preview?window_days=7')
      .then(res => {
        if (cancelled) return;
        setData(res.data);
      })
      .catch(() => {
        if (cancelled) return;
        setData({ payload: { total_activations: 0, breakdown: [] }, html: '' });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card data-testid="weekly-activation-tile-loading">
        <CardContent className="p-5">
          <Skeleton className="h-6 w-40 mb-3" />
          <Skeleton className="h-10 w-20 mb-2" />
          <Skeleton className="h-4 w-full" />
        </CardContent>
      </Card>
    );
  }

  const payload = data?.payload || { total_activations: 0, breakdown: [] };
  const total = payload.total_activations || 0;
  const breakdown = payload.breakdown || [];

  return (
    <>
      <Card
        data-testid="weekly-activation-tile"
        className="border-emerald-200 dark:border-emerald-800 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30"
      >
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider text-emerald-700 dark:text-emerald-300">
                <Sparkles className="w-3 h-3" /> Monday Recap Preview
              </div>
              <div className="flex items-baseline gap-2 mt-1">
                <span
                  className="text-3xl font-display font-bold tabular-nums text-emerald-700 dark:text-emerald-200"
                  data-testid="weekly-activations-count"
                >
                  {total}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  new portal{total !== 1 ? 's' : ''} this week
                </span>
              </div>
            </div>
            {data?.html ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPreviewOpen(true)}
                data-testid="weekly-activation-preview-btn"
                className="shrink-0"
              >
                <Mail className="h-3.5 w-3.5 mr-1.5" /> Preview email
              </Button>
            ) : null}
          </div>

          {breakdown.length > 0 ? (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {breakdown.map(b => (
                <span
                  key={b.role}
                  data-testid={`weekly-breakdown-${b.role}`}
                  className="inline-flex items-center gap-1 rounded-full bg-white/70 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 px-2.5 py-1 text-[11px] font-semibold text-emerald-800 dark:text-emerald-200"
                >
                  <span aria-hidden="true">{b.emoji}</span>
                  {b.count} {b.label}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground pt-1 flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              Quiet week — share your invite links to grow the roster.
            </p>
          )}
          <p className="text-[10px] text-muted-foreground pt-1">
            Sent every Monday at 09:00 IST · Powered by Out-Play Intelligence
          </p>
        </CardContent>
      </Card>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="weekly-activation-preview-dialog"
        >
          <DialogHeader>
            <DialogTitle>Monday email preview</DialogTitle>
            <DialogDescription>
              This is exactly what lands in your inbox every Monday morning at 09:00 IST.
            </DialogDescription>
          </DialogHeader>
          <div
            className="border rounded-lg overflow-hidden bg-white"
            data-testid="weekly-activation-preview-body"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: data?.html || '' }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
